import { createElement, useEffect, useMemo, useRef, useState } from 'react';
import { countBy, first, flatMap, map, range, some, sumBy } from 'lodash';
import { Box, HStack, IIconProps, ScrollView, Skeleton, useMediaQuery, View, VStack } from 'native-base';
import { useTranslation } from 'react-i18next';

import { EquipmentInstancesDto } from '@pimm/services/lib/store-equipment';
import { ButtonGroup, Text } from '@pimm/base';
import { countOfflineSensors, matchesEquipmentFilter } from '../_helper';
import { useGetDisplayGroups } from '../hooks/get-display-groups.hook';
import { BatteryEventIcon } from '../icons/battery-event';
import { DoorEventIcon } from '../icons/door-event';
import { TemperatureEventIcon } from '../icons/temperature-event';
import { WifiOffIcon } from '../icons/wifi-off';
import { EquipmentListFilter } from '../types';
import { CollapsibleGroup } from './collapsible-group';

type FilterEventType = EquipmentListFilter;

type EquipmentListProps = {
  displayGroups: ReturnType<typeof useGetDisplayGroups>;
  equipmentFocus?: EquipmentInstancesDto;
  isListView?: boolean;
  siteId?: string;
  utcOffsetInMinutes?: number;
  onChangeEquipment?: (_equipment?: EquipmentInstancesDto) => void;
};

export const EquipmentList = ({ displayGroups, equipmentFocus, isListView, utcOffsetInMinutes, ...props }: EquipmentListProps) => {
  const scrollViewRef = useRef<any>();
  const { t } = useTranslation();
  const [isSmallScreen] = useMediaQuery({ maxWidth: 768 });
  const [filterType, setFilterType] = useState<FilterEventType>('All');
  const [eventItems, setEventItems] =
    useState<{ label?: string; value: FilterEventType; icon?: React.ComponentType; iconProps?: IIconProps; count?: number }[]>();

  const handleSelectEquipment = (_equipment?: EquipmentInstancesDto) => {
    if (props.onChangeEquipment) props.onChangeEquipment(_equipment);
  };

  const handleChangeTab = (_tab: string) => {
    let _equipment;

    if (_tab === 'list') {
      _equipment =
        equipmentFocus ??
        displayGroups.data?.reduce((_instance: EquipmentInstancesDto | undefined, _) => {
          return _instance ?? first(_.equipment);
        }, undefined);
    }
    handleSelectEquipment(_equipment);
  };

  const filteredDisplayGroups = useMemo(() => {
    if (!displayGroups.data) return [];
    if (filterType === 'All') return displayGroups.data;
    return displayGroups.data
      .map(displayGroup => ({
        displayGroup: displayGroup.displayGroup,
        equipment: displayGroup.equipment.filter(equipment => matchesEquipmentFilter(filterType, equipment)),
      }))
      .filter(displayGroup => displayGroup.equipment.length > 0);
  }, [displayGroups.data, filterType]);

  useEffect(() => {
    const equipmentList = flatMap(displayGroups.data, displayGroup => displayGroup.equipment);

    setEventItems([
      { label: 'store_equipment:all', value: 'All' },
      { value: 'Temperature', icon: TemperatureEventIcon, count: countBy(equipmentList, e => matchesEquipmentFilter('Temperature', e)).true ?? 0 },
      { value: 'Offline', icon: WifiOffIcon, iconProps: { size: 'xs' }, count: sumBy(equipmentList, countOfflineSensors) },
      { value: 'Door', icon: DoorEventIcon, iconProps: { size: 'xs' }, count: countBy(equipmentList, e => matchesEquipmentFilter('Door', e)).true ?? 0 },
      { value: 'Battery', icon: BatteryEventIcon, count: countBy(equipmentList, e => matchesEquipmentFilter('Battery', e)).true ?? 0 },
    ]);
  }, [displayGroups.data]);

  useEffect(() => {
    let scrollToPosition = 0;
    let tempCardHeight = 100;
    if (scrollViewRef.current && isListView) {
      scrollViewRef.current.measure(() => {
        const found = some(filteredDisplayGroups, group => {
          return some(group.equipment, equipment => {
            if (equipment.EquipmentName === equipmentFocus?.EquipmentName) {
              return true;
            }
            scrollToPosition++;
            return false;
          });
        });
        if (found) {
          scrollViewRef.current?.scrollTo({ y: scrollToPosition * tempCardHeight, animated: true });
        }
      });
    }
  }, [scrollViewRef, isListView]);

  return (
    <VStack flex={1} w="full">
      <Box
        flexDir={isListView ? 'column' : { base: 'column', lg: 'row' }}
        justifyContent="space-between"
        py={2}
        px={3}
        borderBottomWidth={!isListView ? 1 : 0}
      >
        {!isSmallScreen && (
          <ButtonGroup
            flex={!isListView ? 1 : undefined}
            mb={isListView ? 2 : 0}
            maxW="300px"
            value={isListView ? 'list' : 'grid'}
            onChange={handleChangeTab}
          >
            <ButtonGroup.Item flex={1} maxW="1/2" value="list">
              {t('store_equipment:list_view')}
            </ButtonGroup.Item>
            <ButtonGroup.Item flex={1} maxW="1/2" value="grid">
              {t('store_equipment:grid_view')}
            </ButtonGroup.Item>
          </ButtonGroup>
        )}

        <ButtonGroup
          flex={!isListView ? 1 : undefined}
          maxW={{ base: 'full', lg: !isListView ? 320 : undefined }}
          value={filterType}
          onChange={value => {
            setFilterType(value as FilterEventType);
          }}
        >
          {map(eventItems, _event => {
            return (
              <ButtonGroup.Item
                key={_event.value}
                flex={1}
                value={_event.value}
                isDisabled={(_event.count ?? filteredDisplayGroups.length) <= 0}
              >
                <HStack space={1.5} alignItems="center">
                  {!!_event.icon && (
                    <Box rounded="full" alignItems="center" justifyContent="center" w={5} h={5} bg="gray.600">
                      {createElement<IIconProps>(_event.icon, { size: 22, color: 'white', ..._event.iconProps })}
                    </Box>
                  )}
                  <Text size="md" fontWeight={500} color="gray.800">
                    {_event.label ? t(_event.label) : _event.count}
                  </Text>
                </HStack>
              </ButtonGroup.Item>
            );
          })}
        </ButtonGroup>
      </Box>

      <View flex={1}>
        <ScrollView ref={scrollViewRef} scrollEnabled={displayGroups.isSuccess} px={3} w="full" flex={1}>
          {displayGroups.isPending && (
            <VStack space={4}>
              {range(3).map(section => (
                <VStack key={`section.${section}`} space={2}>
                  <VStack space={1} maxWidth="2/3">
                    <Skeleton w="full" rounded="2xl" h={3} bg="gray.50" />
                    <Skeleton w="2/3" rounded="2xl" h={3} bg="gray.50" />
                  </VStack>

                  <VStack space={2}>
                    {range(3).map(row => (
                      <Box key={`section.${section}.row.${row}`} rounded="xl" p={2} bg="gray.200">
                        <HStack space={4} h={10}>
                          <VStack flex={1} space={1}>
                            <Skeleton w="full" rounded="md" h={3} bg="gray.50" />
                            <Skeleton w="2/3" rounded="md" h={3} bg="gray.50" />
                          </VStack>

                          <VStack space={1} alignItems="flex-end">
                            <Skeleton w="80px" rounded="md" h={3} bg="gray.50" />
                            <Skeleton w="60px" rounded="md" h={3} bg="gray.50" />
                          </VStack>
                        </HStack>

                        <HStack space={2} w="full">
                          {range(4).map(index => (
                            <Skeleton key={`section.${section}.row.${row}.${index}`} flex={1} rounded="md" h={10} bg="gray.50" />
                          ))}
                        </HStack>
                      </Box>
                    ))}
                  </VStack>
                </VStack>
              ))}
            </VStack>
          )}

          {displayGroups.isSuccess && (
            <VStack>
              {map(filteredDisplayGroups, _group => {
                return (
                  <CollapsibleGroup
                    key={_group.displayGroup}
                    instanceId={equipmentFocus?.TemperatureMetrics?.Temperature?.InstanceId}
                    items={_group.equipment}
                    orientation={isListView ? 'vertical' : undefined}
                    title={_group.displayGroup}
                    utcOffsetInMinutes={utcOffsetInMinutes}
                    onPressItem={handleSelectEquipment}
                  />
                );
              })}
            </VStack>
          )}
        </ScrollView>
      </View>
    </VStack>
  );
};
